import { Chemistry, DeliveryTruck } from "@carbon/icons-react";
import type { TFunction } from "i18next";
import type { RouteObject } from "react-router";
import { buildEntitySection } from "src/config/entitySectionRoutes";
import { lazyRoute } from "src/config/lazyRoutes";
import type { SidebarSection } from "src/general/layout/MainMenu/menuUtils";
import { ACCESS_RULES } from "src/utils/permissionUtils";

/**
 * Sample feature constants
 */
export const SAMPLES = {
  url: "/data/samples",
  icon: Chemistry,
};

/**
 * Sample transfer constants. Transfers move samples into in_transit locations;
 * viewing or editing a transferred sample uses the regular sample pages.
 */
export const SAMPLE_TRANSFERS = {
  url: "/data/sample-transfers",
  icon: DeliveryTruck,
};

/**
 * Sample feature Mainmenu section
 */
export const getSampleMenuSection = (t: TFunction): SidebarSection => ({
  key: "samples",
  title: t("FEAT::samples::Samples"),
  items: [
    {
      key: "samples",
      href: SAMPLES.url,
      icon: SAMPLES.icon,
      title: t("FEAT::samples::Samples"),
      access: ACCESS_RULES.sensorsRead,
    },
    {
      key: "sampleTransfers",
      href: SAMPLE_TRANSFERS.url,
      icon: SAMPLE_TRANSFERS.icon,
      title: t("FEAT::samples::Transfer"),
      access: ACCESS_RULES.sensorsRead,
    },
  ],
});

/**
 * Sample feature routes.
 * Uses lazy loading for code splitting.
 * The feature flag check (FEAT.samples) is in router.tsx. A function rather
 * than a constant: an array built by a call survives tree-shaking, which would
 * pull the feature into the bundle even with the flag off.
 */
export const getSampleRoutes = (): RouteObject[] => [
  buildEntitySection({
    path: SAMPLES.url,
    icon: SAMPLES.icon,
    label: (t: TFunction) => t("FEAT::samples::Samples"),
    list: lazyRoute(() => import("src/features/samples/SampleList")),
    details: lazyRoute(() => import("src/features/samples/SampleDetails")),
    form: lazyRoute(() => import("src/features/samples/SampleForm")),
    entityLabel: () =>
      import("./sampleHooks").then((module) => module.useSampleCrumbLabel),
    access: {
      read: ACCESS_RULES.sensorsRead,
      write: ACCESS_RULES.sensorsWrite,
    },
  }),
  {
    path: SAMPLE_TRANSFERS.url,
    handle: {
      crumb: {
        icon: SAMPLE_TRANSFERS.icon,
        label: (t: TFunction) => t("FEAT::samples::Transfer"),
      },
    },
    children: [
      {
        index: true,
        lazy: lazyRoute(() => import("src/features/samples/TransferList")),
        handle: { access: ACCESS_RULES.sensorsRead },
      },
      {
        path: "add",
        lazy: lazyRoute(() => import("src/features/samples/TransferForm")),
        handle: {
          access: ACCESS_RULES.sensorsWrite,
          crumb: { label: (t: TFunction) => t("FEAT::samples::Add Transfer") },
        },
      },
    ],
  },
];
